import React from 'react';
import { CheckCircle2, Mail, FileText } from 'lucide-react';

const chipTone = (c) => {
  if (c.filled <= 0) return 'bg-white border border-dashed border-gray-300 text-gray-400';
  if (c.filled >= c.capacity) return 'bg-amber-100 text-amber-800';
  return 'bg-emerald-100 text-emerald-700';
};

const LETTER_MARK = {
  signed: { Icon: CheckCircle2, cls: 'text-emerald-700', label: 'Industry letter signed' },
  viewed: { Icon: Mail, cls: 'text-sky-700', label: 'Industry letter viewed, awaiting signature' },
  sent: { Icon: Mail, cls: 'text-amber-700', label: 'Industry letter sent, awaiting signature' },
  draft: { Icon: FileText, cls: 'text-gray-500', label: 'Industry letter drafted, not sent' },
  not_created: { Icon: FileText, cls: 'text-red-500', label: 'No industry letter for this cycle yet' },
};

const LetterMark = ({ c, testIdPrefix }) => {
  if (c.filled <= 0) return null;
  const m = LETTER_MARK[c.industry_letter_status] || LETTER_MARK.not_created;
  return (
    <span
      data-testid={`${testIdPrefix}-letter-${c.cycle_id}`}
      data-status={c.industry_letter_status || 'not_created'}
      title={m.label}
      className={`shrink-0 inline-flex items-center border-l border-current/20 pl-1 ml-0.5 ${m.cls}`}
    >
      <m.Icon className="w-3 h-3" />
    </span>
  );
};

/**
 * Per-cycle fill chips for one restaurant. Active cycles are shown as
 * individual chips; non-active cycles with placements collapse into one
 * muted chip. When `filterCycleId` is set only that cycle is rendered.
 */
export const CycleChips = ({ cycles = [], total = 0, filterCycleId, onChipClick, testIdPrefix = 'cycle-chip' }) => {
  const list = filterCycleId ? cycles.filter((c) => c.cycle_id === filterCycleId) : cycles;
  const active = list.filter((c) => c.status === 'active');
  const others = list.filter((c) => c.status !== 'active' && c.filled > 0);

  if (list.length === 0) {
    return <span className="text-[11px] text-gray-400 italic" data-testid={`${testIdPrefix}-none`}>Not in any cycle</span>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {active.length === 0 && (
        <span className="text-[11px] text-gray-400 italic">Not in any active cycle</span>
      )}
      {active.map((c) => (
        <button
          key={c.cycle_id}
          type="button"
          data-testid={`${testIdPrefix}-${c.cycle_id}`}
          title={`${c.name} — ${c.filled} of ${c.capacity} seats filled · industry letter: ${(c.industry_letter_status || 'not_created').replace('_', ' ')}`}
          onClick={(e) => { e.stopPropagation(); onChipClick?.(c); }}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium max-w-[320px] transition-transform hover:scale-[1.03] ${chipTone(c)}`}
        >
          <span className="truncate">{c.name}</span>
          <b className="shrink-0">{c.filled}/{c.capacity}</b>
          {c.filled >= c.capacity && c.capacity > 0 && <span className="shrink-0">· full</span>}
          <LetterMark c={c} testIdPrefix={testIdPrefix} />
        </button>
      ))}
      {others.length > 0 && (
        <span
          data-testid={`${testIdPrefix}-closed`}
          title={others.map((c) => `${c.name} (${c.status}) — ${c.filled}`).join('\n')}
          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-600"
        >
          +{others.length} {others.length === 1 ? 'closed cycle' : 'closed cycles'}
        </span>
      )}
      {!filterCycleId && (
        <span className="text-[11px] text-gray-400 ml-0.5" data-testid={`${testIdPrefix}-total`}>· {total} total</span>
      )}
    </div>
  );
};

export default CycleChips;
